/* eslint-disable camelcase */
import { useEffect } from 'react';

type GoogleUxMode = 'popup' | 'redirect';

interface GoogleAuthApiInitParams {
  client_id: string;
  cookie_policy?: 'single_host_origin' | 'none';
  fetch_basic_profile: boolean;
  ux_mode?: GoogleUxMode;
}

interface GoogleBasicProfile {
  getId(): string;
  getName(): string;
  getGivenName(): string;
  getFamilyName(): string;
  getImageUrl(): string;
  getEmail(): string;
}

interface GoogleAuthResponse {
  access_token: string;
  id_token: string;
  scope: string;
  expires_in: number;
  first_issued_at: number;
  expires_at: number;
}

interface GoogleUser {
  getBasicProfile(): GoogleBasicProfile;
  getAuthResponse(): GoogleAuthResponse;
}

interface GoogleSignInOptions {
  scope: string;
  ux_mode?: GoogleUxMode;
}

interface GoogleAuth {
  signIn(options: GoogleSignInOptions): Promise<GoogleUser>;
}

interface GoogleAuth2Api {
  init(params: GoogleAuthApiInitParams): {
    then(
      onInit: (googleAuth: GoogleAuth) => void,
      onError?: (err: Error) => void
    ): void;
  };
  getAuthInstance(): GoogleAuth;
}

export interface GoogleApi {
  load(apiType: 'auth2', callback: () => void): void;
  auth2?: GoogleAuth2Api;
}

declare global {
  interface Window {
    gapi?: GoogleApi;
  }
}

interface Props {
  clientId: string;
  onLoad: () => void;
}

export function GoogleSdkLoader(props: Props): JSX.Element {
  let dispatchEvents = false;

  const elementId = 'google-sdk';

  const dispatchLoadEvent = (): void => {
    if (dispatchEvents) {
      props.onLoad();
    }
  };

  const loadAuth2Api = (callback: ()=> void): void => {
    window.gapi.load('auth2', () => callback());
  };

  const loadGoogleApi = ():void => {
    if (!window.gapi) {
      return null;
    }

    if (!window.gapi.auth2) {
      return loadAuth2Api(() => loadGoogleApi());
    }

    const authInstance = window.gapi.auth2.getAuthInstance();
    if (!authInstance) {
      return window.gapi.auth2.init({
        client_id: props.clientId,
        cookie_policy: 'single_host_origin',
        fetch_basic_profile: true,
      }).then(() => {
        dispatchLoadEvent();
      });
    }

    return dispatchLoadEvent();
  };

  const importGoogleSdk = (): boolean => {
    if (document.getElementById(elementId)) {
      return false;
    }

    const script = document.createElement('script');
    script.id = elementId;
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = loadGoogleApi;
    document.body.appendChild(script);
    return true;
  };

  useEffect(() => {
    dispatchEvents = true;

    if (!importGoogleSdk()) {
      loadGoogleApi();
    }

    return () => {
      dispatchEvents = false;
    };
  }, []);

  return null;
}
